import React, { useState } from "react";

const CopyToClipboard = ({ text }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      onClick={handleCopy}
      className="border_none bg_transparent cursor_pointer p_1 opacity_50 hover_bg_light border_radius_small"
      title="Copy to clipboard"
    >
      {copied ? (
        <span className="font_size_xsmall color_secondary font_weight_bold">Copied!</span>
      ) : (
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ display: "block" }}
        >
          <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
        </svg>
      )}
    </button>
  );
};

const PreWithCopy = ({ label, value }) => {
  const jsonString = typeof value === "string" ? value : JSON.stringify(value, null, 2);

  return (
    <div className="d_flex flex_column gap_1 w_100 position_relative mt_2">
      {label && <span className="font_size_xsmall font_weight_bold no_select">{label}</span>}

      <div className="position_relative w_100">
        <pre
          className="bg_gray p_2 border_radius_small overflow_auto m_0 font_size_xsmall w_100"
          style={{ maxHeight: "350px", fontFamily: "monospace", color: "#333" }}
        >
          {jsonString || "(empty)"}
        </pre>

        <div className="position_absolute top_0 right_0 p_1">
          <CopyToClipboard text={jsonString} />
        </div>
      </div>
    </div>
  );
};

const InfoItem = ({ label, value }) => (
  <div className="d_flex gap_2 w_100">
    <span className="font_size_xsmall">{label}:</span>
    <span className="font_size_small font_weight_bold " style={{ fontFamily: "monospace", wordBreak: "break-all" }}>
      {value}
    </span>
  </div>
);

const HttpLogView = ({ parsedData }) => {
  const httpData = parsedData.data || parsedData.entry?.data || {};
  const req = httpData.request || {};
  const res = httpData.response || {};

  // 1. Get status from Root OR Response object
  const rawStatus = parsedData.http_status_code || res.status_code || res.status;
  const statusCode = rawStatus || "---";

  // 2. Robust Error Check: Handles Number(404) and String("404 Not Found")
  const isError =
    rawStatus && (Number(rawStatus) >= 400 || String(rawStatus).startsWith("4") || String(rawStatus).startsWith("5"));

  const elapsedMs = parsedData.entry?.elapsed_ms || parsedData.elapsed_ms;
  const statusColor = isError ? "#d32f2f" : "#2e7d32";

  return (
    <div className="d_flex flex_column gap_4 w_100 pt_2">
      {/* Header Info */}
      <div className="border_full p_2 border_radius_medium">
        <div className="d_flex align_center justify_between mb_1">
          <span className="font_weight_bold font_size_medium">{req.method || "HTTP"}</span>
          <div className="d_flex align_center gap_2">
            {elapsedMs && <span className="font_size_xsmall color_secondary">{elapsedMs}ms</span>}
            <span className="font_weight_normal font_size_medium" style={{ color: statusColor }}>
              {statusCode}
            </span>
          </div>
        </div>
        <div className="font_size_small color_secondary" style={{ wordBreak: "break-all" }}>
          {req.url}
        </div>
      </div>

      <div className="d_flex flex_column gap_3">
        <PreWithCopy label="Request" value={req} />
        <PreWithCopy label="Response" value={res} />
      </div>
    </div>
  );
};

const ScreenLogView = ({ parsedData }) => (
  <div className="d_flex flex_column gap_4 w_100 pt_2">
    <div className="border p_2 border_radius_medium">
      <div className="font_size_xsmall color_secondary text_transform_uppercase mb_1">Screen</div>
      <div className="font_size_medium font_weight_bold color_primary">
        {parsedData.screen_name || "Unknown Screen"}
      </div>
    </div>

    <div className="d_flex flex_column gap_3">
      <InfoItem label="IP Address" value={parsedData.ip_address || "N/A"} />
      {/* <InfoItem label="User Agent" value={parsedData.user_agent || "N/A"} /> */}
    </div>

    <PreWithCopy label="Full Context" value={parsedData} />
  </div>
);

const ActivityLogView = ({ parsedData, type }) => {
  const isForm = type === "form";
  const entryData = parsedData.entry || parsedData;
  const title = entryData.msg || (isForm ? "Form Submission" : "Activity");
  const name = entryData.sm_name || entryData.job_name || "Unknown Task";
  const duration = entryData.duration;

  return (
    <div className="d_flex flex_column gap_4 w_100 pt_2">
      <div className="border p_2 border_radius_medium">
        <div className="d_flex justify_between align_start">
          <span className="font_size_xsmall color_secondary text_transform_uppercase mb_1">{title}</span>
          {duration && (
            <span className="font_size_xsmall color_secondary" style={{ fontFamily: "monospace" }}>
              {duration}
            </span>
          )}
        </div>
        <div className="font_size_medium font_weight_medium color_primary">{name}</div>
      </div>

      <PreWithCopy label="Data" value={parsedData} />
    </div>
  );
};

export { HttpLogView, ScreenLogView, ActivityLogView, PreWithCopy };
